<script setup lang="ts">
    import { computed, onMounted, watch, shallowReactive, nextTick, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import { MonthYear } from '@common/types'

    import { useScheduledProjectsPageQueryV1 } from '@Quest/Portfolios/queries-vue-composables'
    import { useSaveViewScheduledProjectsPageCommandV1 } from '@Quest/Portfolios/commands-vue-composables'

    import { useGridProjectByIdQueryV1 } from '@Quest/Projects/queries-vue-composables'

    import
        {
            useSetLifecycleInProgressCommandV1,
            useSetLifecycleProposalCommandV1,
            useSetLifecycleQualifiedCommandV1,
            useSetLifecycleRejectedCommandV1,
            useSetLifecycleScheduledCommandV1
        } from '@Quest/Projects/commands-vue-composables'

    import { ScheduledProjectsPageReadModel } from '@Quest/Portfolios/readmodels'
    import { GridProjectReadModel } from '@Quest/Projects/readmodels'

    import { ProjectsGridViewModel } from '@Quest/Projects/Components/ProjectsGrid'
    import ProjectsGrid from '@Quest/Projects/Components/ProjectsGrid.vue'

    import { ProjectProposalLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectProposalLifecycleSetter'
    import ProjectProposalLifecycleSetter from '@Quest/Projects/Components/ProjectProposalLifecycleSetter.vue'

    import { ProjectQualifiedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectQualifiedLifecycleSetter'
    import ProjectQualifiedLifecycleSetter from '@Quest/Projects/Components/ProjectQualifiedLifecycleSetter.vue'

    import { ProjectRejectedLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectRejectedLifecycleSetter'
    import ProjectRejectedLifecycleSetter from '@Quest/Projects/Components/ProjectRejectedLifecycleSetter.vue'

    import { ProjectScheduledLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectScheduledLifecycleSetter'
    import ProjectScheduledLifecycleSetter from '@Quest/Projects/Components/ProjectScheduledLifecycleSetter.vue'

    import { ProjectInProgressLifecycleSetterViewModel } from '@Quest/Projects/Components/ProjectInProgressLifecycleSetter'
    import ProjectInProgressLifecycleSetter from '@Quest/Projects/Components/ProjectInProgressLifecycleSetter.vue'



    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))



    // ======================
    // Query
    // ======================

    const {
        runScheduledProjectsPageQueryV1,
        modelScheduledProjectsPageQueryV1
    } = useScheduledProjectsPageQueryV1(workspaceId, tchProjectId)

    const rm = modelScheduledProjectsPageQueryV1 as Ref<ScheduledProjectsPageReadModel | null>

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const portfolioName = computed(() => rm.value?.PortfolioName ?? null)



    // ======================
    // Local Success Caches
    // ======================

    const lastProposalId = ref<string | null>(null)
    const lastQualifiedId = ref<string | null>(null)
    const lastRejectedId = ref<string | null>(null)
    const lastScheduledId = ref<string | null>(null)
    const lastInProgressId = ref<string | null>(null)



    // ======================
    // Grid project query
    // ======================

    const {
        runGridProjectByIdQueryV1,
        modelGridProjectByIdQueryV1
    } = useGridProjectByIdQueryV1(workspaceId, portfolioId)



    // ======================
    // Commands
    // ======================

    const {
        invokeSaveViewScheduledProjectsPageCommandV1,
        succeedSaveViewScheduledProjectsPageCommandV1
    } = useSaveViewScheduledProjectsPageCommandV1(workspaceId, portfolioId)

    const {
        invokeSetLifecycleProposalCommandV1,
        succeedSetLifecycleProposalCommandV1
    } = useSetLifecycleProposalCommandV1(workspaceId, portfolioId, lastProposalId)

    const {
        invokeSetLifecycleQualifiedCommandV1,
        succeedSetLifecycleQualifiedCommandV1
    } = useSetLifecycleQualifiedCommandV1(workspaceId, portfolioId, lastQualifiedId)

    const {
        invokeSetLifecycleRejectedCommandV1,
        succeedSetLifecycleRejectedCommandV1
    } = useSetLifecycleRejectedCommandV1(workspaceId, portfolioId, lastRejectedId)

    const {
        invokeSetLifecycleScheduledCommandV1,
        succeedSetLifecycleScheduledCommandV1
    } = useSetLifecycleScheduledCommandV1(workspaceId, portfolioId, lastScheduledId)

    const {
        invokeSetLifecycleInProgressCommandV1,
        succeedSetLifecycleInProgressCommandV1
    } = useSetLifecycleInProgressCommandV1(workspaceId, portfolioId, lastInProgressId)



    // ======================
    // ViewModels
    // ======================

    const grid = shallowReactive(new ProjectsGridViewModel())

    const projectQualifiedLifecycleSetter = shallowReactive(new ProjectQualifiedLifecycleSetterViewModel())
    const projectRejectedLifecycleSetter = shallowReactive(new ProjectRejectedLifecycleSetterViewModel())
    const projectProposalLifecycleSetter = shallowReactive(new ProjectProposalLifecycleSetterViewModel())
    const projectScheduledLifecycleSetter = shallowReactive(new ProjectScheduledLifecycleSetterViewModel())
    const projectInProgressLifecycleSetter = shallowReactive(new ProjectInProgressLifecycleSetterViewModel())



    // ======================
    // Handlers
    // ======================

    function handleShowGridColumnsEditor()
    {
        grid.showGridColumnsEditor()
    }

    function handleSaveViewSettings()
    {
        invokeSaveViewScheduledProjectsPageCommandV1({
            Grid: grid.getJson()
        })
    }

    function handleClearGridFilters()
    {
        grid.clearFilters()
    }

    function handleExportGrid()
    {
        grid.exportGrid()
    }

    function handleProposed(projectId: string)
    {
        lastProposalId.value = projectId
        invokeSetLifecycleProposalCommandV1()
    }

    function handleQualified(projectId: string)
    {
        lastQualifiedId.value = projectId
        invokeSetLifecycleQualifiedCommandV1()
    }

    function handleRejected(projectId: string)
    {
        lastRejectedId.value = projectId
        invokeSetLifecycleRejectedCommandV1()
    }

    function handleScheduled(projectId: string, startDate: MonthYear)
    {
        lastScheduledId.value = projectId
        invokeSetLifecycleScheduledCommandV1({ StartDate: startDate })
    }

    function handleStarted(projectId: string, startDate: MonthYear)
    {
        lastInProgressId.value = projectId
        invokeSetLifecycleInProgressCommandV1({ StartDate: startDate })
    }



    // ======================
    // Query Watchers
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        nextTick(() =>
        {
            grid.renderGrid(
                current.Grid.IdField,
                current.Grid.Fields,
                current.Grid.DefaultFieldIds,
                current.Grid.Options
            )
            grid.setProjects(current.Projects.map(p => p.Properties))
        })
    })

    watch(modelGridProjectByIdQueryV1 as Ref<GridProjectReadModel | null>, (current) =>
    {
        if (!current || !rm.value) return

        rm.value.Projects = rm.value.Projects.filter(p => p.Id !== current.Id)
        rm.value.Projects.push(current)

        grid.update(current.Properties)
    })



    // ======================
    // Command Watchers
    // ======================

    watch(succeedSaveViewScheduledProjectsPageCommandV1, (c, p) =>
    {
        if (c && !p) alert('Saved')
    })

    watch(succeedSetLifecycleProposalCommandV1, (c, p) =>
    {
        if (c && !p && lastProposalId.value && rm.value)
        {
            const id = lastProposalId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectProposalLifecycleSetter.close()
            lastProposalId.value = null
        }
    })

    watch(succeedSetLifecycleQualifiedCommandV1, (c, p) =>
    {
        if (c && !p && lastQualifiedId.value && rm.value)
        {
            const id = lastQualifiedId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectQualifiedLifecycleSetter.close()
            lastQualifiedId.value = null
        }
    })

    watch(succeedSetLifecycleRejectedCommandV1, (c, p) =>
    {
        if (c && !p && lastRejectedId.value && rm.value)
        {
            const id = lastRejectedId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectRejectedLifecycleSetter.close()
            lastRejectedId.value = null
        }
    })

    watch(succeedSetLifecycleScheduledCommandV1, (c, p) =>
    {
        if (c && !p && lastScheduledId.value)
        {
            runGridProjectByIdQueryV1({ ProjectId: lastScheduledId.value })
            projectScheduledLifecycleSetter.close()
            lastScheduledId.value = null
        }
    })

    watch(succeedSetLifecycleInProgressCommandV1, (c, p) =>
    {
        if (c && !p && lastInProgressId.value && rm.value)
        {
            const id = lastInProgressId.value
            rm.value.Projects = rm.value.Projects.filter(a => a.Id !== id)
            grid.removeRowById(id)
            projectInProgressLifecycleSetter.close()
            lastInProgressId.value = null
        }
    })



    // ======================
    // Lifecycle
    // ======================

    onMounted(() =>
    {
        document.title = "Portfolio Scheduled | TransparentChoice"
        runScheduledProjectsPageQueryV1()
    })
</script>

<template>
    <div>
        <ProjectProposalLifecycleSetter :viewModel="projectProposalLifecycleSetter" @confirm="handleProposed" />
        <ProjectQualifiedLifecycleSetter :viewModel="projectQualifiedLifecycleSetter" @confirm="handleQualified" />
        <ProjectRejectedLifecycleSetter :viewModel="projectRejectedLifecycleSetter" @confirm="handleRejected" />
        <ProjectScheduledLifecycleSetter :viewModel="projectScheduledLifecycleSetter" @confirm="handleScheduled" />
        <ProjectInProgressLifecycleSetter :viewModel="projectInProgressLifecycleSetter" @confirm="handleStarted" />

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <h6 class="text-muted mt-2">{{portfolioName}}</h6>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Scheduled projects</h1>
                </div>
                <div class="col-lg-6 text-right align-self-end">
                    <button role="button" class="btn btn-outline-secondary" style="padding: 0.7em 2em;" @click="handleSaveViewSettings">Save</button>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div class="toolbar">
                <div>
                    <!--<button type="button" class="btn btn-outline-secondary">Action ({{selectedIds.length}})</button>-->
                </div>
                <div>
                    <button type="button" class="btn btn-outline-secondary mr-1" @click="handleShowGridColumnsEditor">Edit columns</button>

                    <div class="btn-group mr-1">
                        <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                            Actions
                        </button>
                        <div class="dropdown-menu dropdown-menu-right">
                            <a class="dropdown-item" @click="handleClearGridFilters">Clear Filters</a>
                        </div>
                    </div>

                    <button type="button" class="btn btn-outline-secondary" @click="handleExportGrid" title="Download to spreadsheet">
                        <i class="fa fa-download fa-lg"></i>
                    </button>
                </div>
            </div>

            <ProjectsGrid :viewModel="grid" />
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>